import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authClient, UserDto, LoginRequest, RegisterRequest } from '../services/authClient';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorHelpers';

interface AuthContextType {
  user: UserDto | null;
  loading: boolean;
  login: (request: LoginRequest) => Promise<void>;
  register: (request: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserDto | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if user is already logged in on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const currentUser = await authClient.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (request: LoginRequest) => {
    try {
      const response = await authClient.login(request);

      // Check if SSO requires a redirect (registration, password change, etc.)
      if (response.redirectUrl) {
        // Redirect to SSO - don't continue with normal login flow
        window.location.href = response.redirectUrl;
        return;
      }

      setUser({
        id: response.userId,
        email: response.email,
        userName: response.userName,
        roles: response.roles || [],
      });
      toast.success('Login successful!');

      // Check if there's a redirect path stored
      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      if (redirectPath) {
        sessionStorage.removeItem('redirectAfterLogin');
        window.location.href = redirectPath;
      } else {
        window.location.href = '/';
      }
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Login failed'));
      throw error;
    }
  };

  const register = async (request: RegisterRequest) => {
    try {
      const response = await authClient.register(request);
      setUser({
        id: response.userId,
        email: response.email,
        userName: response.userName,
      });
      toast.success('Registration successful!');
      window.location.href = '/';
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Registration failed'));
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authClient.logout();
      setUser(null);
      toast.success('Logged out successfully');
      window.location.href = '/login';
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Logout failed'));
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
